// src/components/RH/Components/Catalogos/EmpleadosTurnos/EmpleadosTurnosCatalog.jsx
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../../General/Utils/Endpoints';
import { Models } from '../../../../General/Utils/ModelsGeneral'; // Correct model for Employees
import EmpleadosTurnosTab from '../../../../RH/Components/Tabs/EmpleadosTurnosTab'; // D&D Component
import { normalizeValue } from '../../../../../utils/Texts';
import sigmaEdit from '../../../../Pages/img/Editar.png';

const PAGE_SIZE = 10;
const nombreTabla = 'Gestión de Turnos por Empleado';
const endpoint = ENDPOINTS.empleados; // List employees instead of links
const modelo = Models.empleado;

export default function EmpleadosTurnosCatalog() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');

  // Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const [page, setPage] = useState(1);
  const [personasMap, setPersonasMap] = useState(new Map());

  // 1. Load Employees
  const loadData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(endpoint);
      setRows((res.data || []).map((x) => modelo(x)));
    } catch (e) {
      console.error(e);
      alert('Error al cargar empleados');
    } finally {
      setLoading(false);
    }
  };

  // 2. Load Personas for Name resolution
  const loadPersonas = async () => {
    try {
      const res = await axios.get(ENDPOINTS.personas);
      const map = new Map();
      (res.data || []).forEach(pRaw => {
        const p = Models.personas(pRaw);
        map.set(p.idPersona, `${p.nombre} ${p.apellidoPaterno} ${p.apellidoMaterno || ''}`.trim());
      });
      setPersonasMap(map);
    } catch (e) {
      console.error('Error loading personas', e);
    }
  };

  useEffect(() => {
    loadData();
    loadPersonas();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const numero = String(r.numeroEmpleado || '').toLowerCase();
      const nombre = (personasMap.get(r.idPersona) || '').toLowerCase();
      return numero.includes(q) || nombre.includes(q);
    });
  }, [rows, query, personasMap]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [totalPages, page]);

  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const handleEdit = (id) => {
    setSelectedId(id);
    setModalOpen(true);
  };

  const PageBtn = ({ label, onClick, active, disabled }) => (
    <button
      type="button"
      className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
      onClick={onClick}
      disabled={disabled}
    >
      {label}
    </button>
  );

  const pageNumbers = (() => {
    const maxToShow = 5;
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + maxToShow - 1);
    start = Math.max(1, end - maxToShow + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  })();

  const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(page * PAGE_SIZE, filtered.length);

  return (
    <div className="panel-sigma-main">
      <div className="roles-shell">
        <div className="roles-topbar">
          <h2 className="roles-title">{nombreTabla}</h2>

          <input
            className="roles-search"
            placeholder="Buscar por empleado..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(1);
            }}
          />
        </div>

        <div className="roles-table-wrap">
          <div className="roles-table-scroller">
            <table className="roles-table">
              <thead>
                <tr>
                  <th>Empleado (Nombre)</th>
                  <th>Número Empleado</th>
                  <th style={{ textAlign: 'center' }}>Gestionar Turnos</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((r) => {
                  const nombre = personasMap.get(r.idPersona) || '(Sin Nombre)';
                  return (
                    <tr key={r.idEmpleado}>
                      <td>{normalizeValue(nombre)}</td>
                      <td><strong>{normalizeValue(r.numeroEmpleado)}</strong></td>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          className="rk-iconbtn rk-iconbtn--edit"
                          onClick={() => handleEdit(r.idEmpleado)}
                          aria-label="Gestionar"
                          title="Gestionar Turnos"
                        >
                          <img src={sigmaEdit} alt="Editar" className="rk-ico" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {!loading && paged.length === 0 && (
                  <tr>
                    <td colSpan={3}>Sin resultados.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="roles-pagination">
          <div className="roles-pagination-left">
            {filtered.length > 0 ? (
              <span>
                Mostrando {startIndex}–{endIndex} de {filtered.length}
              </span>
            ) : (
              <span>0 resultados</span>
            )}
          </div>
          <div className="roles-pagination-right">
            <PageBtn
              label="‹"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            />
            {pageNumbers.map((n) => (
              <PageBtn
                key={n}
                label={n}
                onClick={() => setPage(n)}
                active={n === page}
              />
            ))}
            <PageBtn
              label="›"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            />
          </div>
        </div>

        {modalOpen && (
          <div
            className="roles-confirm-overlay"
            role="dialog"
            aria-modal="true"
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(15,23,42,.45)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 16,
              zIndex: 1000,
            }}
            onClick={(e) => { if (e.target === e.currentTarget) setModalOpen(false); }}
          >
            <div
              className="roles-confirm-card"
              style={{
                width: 'min(900px, 96vw)',
                maxHeight: '85vh',
                background: '#fff',
                borderRadius: 12,
                boxShadow: '0 10px 30px rgba(0,0,0,.25)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                padding: '1rem', // Add padding for the internal component
              }}
            >
              {/** Use the D&D component directly. Access the idEmpleado via 'selectedId' */}
              <EmpleadosTurnosTab
                fkValue={selectedId}
                onClose={() => setModalOpen(false)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

